// src/utils/apiClient.js
const configApiUrl = (window.APP_CONFIG && window.APP_CONFIG.ApiBaseUrl) ? window.APP_CONFIG.ApiBaseUrl : null;
const envApiUrl = process.env.REACT_APP_API_BASE_URL;
const isLocalBrowser = typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname);

// Production veya env tanımlı ise her zaman canlı API'yi kullan
let API_URL = envApiUrl || configApiUrl || (isLocalBrowser ? 'http://localhost:5179' : 'https://api.xpass.com.tr');

const finalApiUrl = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;

export const BASE_URL = finalApiUrl;

// Token gerektirmeyen, herkese açık endpoint'ler
const PUBLIC_ENDPOINTS = [
    'Auth/login',
    'Auth/admin-login',
    'Auth/mfa/setup',
    'Auth/mfa/enable',
    'Auth/mfa/complete',
    'Auth/refresh-web', // Madde 6: Cookie based refresh
    'Auth/logout-web',  // Madde 6: Logout
];

// --- firmCode'u birden çok kaynaktan okuyan helper ---
function getFirmCodeSafe() {
    try {
        const ls = localStorage.getItem('firmCode');
        if (ls) return ls;
    } catch { /* ignore */ }

    try {
        const ss = sessionStorage.getItem('firmCode');
        if (ss) return ss;
    } catch { /* ignore */ }

    // Opsiyonel: global değişken
    if (typeof window !== 'undefined' && window.__FIRM_CODE__) {
        return window.__FIRM_CODE__;
    }

    return '';
}

function buildModelStateMessage(errorsObj) {
    if (!errorsObj || typeof errorsObj !== 'object') return null;
    const parts = [];
    for (const [field, arr] of Object.entries(errorsObj)) {
        if (Array.isArray(arr) && arr.length) {
            parts.push(`${field}: ${arr.join(' | ')}`);
        }
    }
    return parts.length ? parts.join('\n') : null;
}

// --- JWT'den firmCode çıkaran helper ---
function getFirmCodeFromToken(token) {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const payload = JSON.parse(jsonPayload);
        return payload.firmCode || null;
    } catch (e) {
        console.error('Token decode hatası:', e);
        return null;
    }
}

export const apiClient = async (endpoint, options = {}) => {
    if (typeof options === 'string' || options === null) {
        options = {};
    }
    const headers = { ...options.headers };
    if (!options.isFormData && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    const token = localStorage.getItem('authToken');
    const tokenFirmCode = getFirmCodeFromToken(token);
    const storageFirmCode = getFirmCodeSafe();

    // Holding - Active Sub Company Code
    let activeSubCompanyCode = null;
    try {
        activeSubCompanyCode = localStorage.getItem('activeSubCompanyCode');
    } catch { /* ignore */ }

    // Öncelik: Token içindeki firmCode. Eğer yoksa storage'dakini kullan.
    const firmCode = tokenFirmCode || storageFirmCode;

    // Public DEĞİLSE token & firma kodu zorunlu
    if (!PUBLIC_ENDPOINTS.includes(endpoint)) {
        if (!token || !firmCode) {
            const err = new Error('Bu işlemi yapmak için giriş yapmalısınız veya firma seçimi eksik.');
            err.status = 401;
            throw err;
        }
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Firm-Code'] = firmCode;
        if (activeSubCompanyCode) {
            headers['X-Active-SubCompany-Code'] = activeSubCompanyCode;
        }
    } else {
        // Public endpoint ise ama değerler varsa yine header'a ekle (refresh ve logout HARİÇ)
        if (firmCode) headers['X-Firm-Code'] = firmCode;
        if (activeSubCompanyCode) headers['X-Active-SubCompany-Code'] = activeSubCompanyCode;
        if (token && endpoint !== 'Auth/refresh-web' && endpoint !== 'Auth/logout-web') {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    // ---- Query string ----
    let queryString = '';
    if (options?.queryParams) {
        const usp = new URLSearchParams();
        Object.entries(options.queryParams).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') usp.append(k, v);
        });
        const qs = usp.toString();
        if (qs) queryString = (endpoint.includes('?') ? '&' : '?') + qs;
    }

    const config = {
        ...options,
        method: options.method || 'GET',
        headers,
        credentials: 'include' // Madde 6: Cookie based refresh requires this
    };
    if (config.body && typeof config.body !== 'string' && !options.isFormData) {
        config.body = JSON.stringify(config.body);
    }
    if (config.method === 'GET' && config.body) delete config.body;

    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${finalApiUrl}/${normalizedEndpoint}${queryString}`;
    let res = await fetch(url, config);

    // Blob indirme (raporlar vs.)
    if (options.responseType === 'blob') {
        if (!res.ok) {
            const err = new Error(`Dosya indirilirken bir hata oluştu: ${res.status} ${res.statusText}`);
            err.status = res.status;
            throw err;
        }
        const blob = await res.blob();
        const disposition = res.headers.get('Content-Disposition');
        const ct = res.headers.get('content-type') || '';
        let filename = 'rapor';
        if (ct.includes('pdf')) filename += '.pdf';
        if (ct.includes('spreadsheetml.sheet')) filename += '.xlsx';
        if (disposition && disposition.includes('filename=')) {
            const m = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (m?.[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ''));
        }
        return { blob, filename };
    }
    // 204 No Content
    if (res.status === 204) return {};

    // --- Madde 6: Token Refresh Logic (401 Interceptor) ---
    if (res.status === 401 && !PUBLIC_ENDPOINTS.includes(normalizedEndpoint)) {
        // Intercept 401 attempt refresh
        try {
            // Check if it's SESSION_INVALID (TokenVersion mismatch)
            // Need to parse body to check error code
            const clone = res.clone();
            const payload = await clone.json().catch(() => ({}));

            if (payload.code === 'SESSION_INVALID') {
                console.warn('Session invalidated (TokenVersion mismatch). Logging out.');
                window.dispatchEvent(new CustomEvent('auth:logout', { detail: { message: payload.message } }));
                const err = new Error(payload.message || 'Oturumunuz yenilendi. Tekrar giriş yapın.');
                err.status = 401;
                err.code = 'SESSION_INVALID';
                throw err;
            }

            // Silent Refresh (Cookie based)
            const refreshRes = await fetch(`${finalApiUrl}/Auth/refresh-web`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Firm-Code': firmCode
                },
                credentials: 'include'
            });

            if (refreshRes.ok) {
                const refreshData = await refreshRes.json();
                const newToken = refreshData.accessToken;

                if (newToken) {
                    localStorage.setItem('authToken', newToken);
                    // Retry original request
                    headers['Authorization'] = `Bearer ${newToken}`;
                    const retryRes = await fetch(url, { ...config, headers });
                    res = retryRes; // Swap res with retry result
                    if (res.ok) {
                        // Parse and return if retry succeeded
                        if (res.status === 204) return {};
                        return await res.json();
                    }
                }
            } else {
                // Refresh failed
                console.warn("[apiClient] Sessiz yenileme başarısız veya token geçersiz. auth:logout tetikleniyor.");
                window.dispatchEvent(new CustomEvent('auth:logout', { detail: { message: 'Oturum süresi doldu.' } }));
            }
        } catch (e) {
            if (e.status !== 401) {
                console.warn('Silent refresh prevented or failed:', e.message);
            }
        }
    }

    // Başarısız yanıtlar
    if (!res.ok) {
        // NOT: response body tek sefer okunabilir. O yüzden önce text alıp sonra parse ediyoruz.
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        let rawText = '';
        try {
            rawText = await res.text();
        } catch {
            rawText = '';
        }

        let payload = null;
        if (rawText && ct.includes('application/json')) {
            try {
                payload = JSON.parse(rawText);
            } catch {
                payload = null;
            }
        }

        // Backend bazen düz string dönebiliyor (BadRequest("mesaj"))
        // veya ProblemDetails / ModelState dönebiliyor.
        const modelStateMsg = payload?.errors ? buildModelStateMessage(payload.errors) : null;

        const payloadMessage =
            typeof payload === 'string'
                ? payload
                : (modelStateMsg || payload?.message || payload?.detail || payload?.title || null);

        const finalMessage =
            (payloadMessage || rawText || '').toString().trim()
            || `API hatası: ${res.status} ${res.statusText}`;

        const err = new Error(finalMessage);
        err.status = res.status;

        // Backend'den gelen ek bilgileri (code, ownerInfo vs.) her durumda ekle
        if (payload && typeof payload === 'object') {
            if (payload.code) err.code = payload.code;
            if (payload.ownerInfo) err.ownerInfo = payload.ownerInfo;
            // Diğer olası alanlar
            err.payload = payload;

            // --- Tenant Isolation UX Patch ---
            if (res.status === 403 && payload.code === 'FIRM_MISMATCH') {
                alert(payload.message || 'Firma uyuşmazlığı tespit edildi. Yeniden giriş yapmalısınız.');

                // Logout logic
                localStorage.removeItem('authToken');
                localStorage.removeItem('firmCode');

                // Redirect to login
                window.location.replace('/login');
                return; // Stop execution
            }
            // ---------------------------------
        }

        throw err;
    }

    // Başarılı yanıt — JSON parse (gövde boş olabilir)
    try {
        return await res.json();
    } catch {
        return {};
    }
};
